// =============================================================================
// SITE CONFIGURATION SCHEMA
// =============================================================================
// This file defines the structure of `site.config.ts` — the master
// configuration file that controls everything about a specific restaurant
// website: which style to use, which integrations are enabled, which
// region's laws apply, which languages are available, etc.
//
// When you create a new restaurant website, you fill in ONE site.config.ts
// file and the entire site adapts to it. No code changes needed.
// =============================================================================

import { z } from 'zod';

// -----------------------------------------------------------------------------
// Delivery Platform Configuration
// -----------------------------------------------------------------------------

/**
 * A delivery platform button that links out to the restaurant's page
 * on that platform. These are shown as a row of logos (e.g., Uber Eats,
 * Deliveroo, iFood) under or beside the "Order Online" CTA.
 *
 * WHY EXTERNAL LINKS (not embeds):
 * Delivery platforms don't offer embeddable ordering widgets — they want
 * users to come to their app. So we just show logo buttons that open
 * the restaurant's page on each platform in a new tab. No third-party
 * scripts needed, no CSP complications, no commission split.
 */
export const DeliveryPlatformSchema = z.object({
  /** Platform identifier — controls which logo/icon is displayed.
   *  Must match one of the supported platform names. */
  name: z.enum([
    // --- North America ---
    'uber-eats', // Uber Eats (US, Canada, EU, LatAm, Asia)
    'doordash', // DoorDash (US, Canada, Australia)
    'grubhub', // Grubhub (US)

    // --- Europe ---
    'deliveroo', // Deliveroo (UK, France, Italy, UAE, etc.)
    'just-eat', // Just Eat / Just Eat Takeaway (UK, EU)
    'glovo', // Glovo (Spain, Italy, Eastern Europe, MENA)
    'wolt', // Wolt (Nordics, Central/Eastern Europe)

    // --- Latin America ---
    'ifood', // iFood (Brazil — largest in the country)
    'rappi', // Rappi (Colombia, Mexico, Brazil, Argentina, etc.)
    'pedidosya', // PedidosYa (Argentina, Uruguay, Chile)

    // --- Asia ---
    'zomato', // Zomato (India)
    'swiggy', // Swiggy (India)
    'grabfood', // GrabFood (Southeast Asia)
    'foodpanda', // foodpanda (SE Asia, some EU)

    // --- Middle East ---
    'talabat', // Talabat (UAE, Saudi Arabia, Kuwait, etc.)
  ]),

  /** Direct URL to the restaurant's page on this platform.
   *  Example: "https://www.ubereats.com/store/trattoria-da-marco/abc123" */
  url: z.string().url('Must be a valid URL to your restaurant page on this platform'),
});
export type DeliveryPlatform = z.infer<typeof DeliveryPlatformSchema>;

// -----------------------------------------------------------------------------
// Analytics Configuration
// -----------------------------------------------------------------------------

/**
 * Analytics provider configuration.
 *
 * TWO PROVIDERS ARE SUPPORTED:
 *
 * 1. CLOUDFLARE WEB ANALYTICS (recommended, free, cookieless):
 *    - Built into Cloudflare Pages — no extra setup needed
 *    - Does NOT use cookies → no GDPR consent required
 *    - Shows: page views, visitors, top pages, countries, referrers
 *    - Enable in Cloudflare Dashboard → your site → Web Analytics
 *
 * 2. GOOGLE ANALYTICS 4 (optional, consent-gated):
 *    - More detailed: conversion tracking, user flows, custom events
 *    - USES COOKIES → REQUIRES consent banner (auto-enabled when GA4 is on)
 *    - Needs a Measurement ID (starts with "G-") from Google Analytics
 */
export const AnalyticsConfigSchema = z.object({
  cloudflare: z
    .object({
      /** Enable Cloudflare Web Analytics — free, no cookies, no consent needed */
      enabled: z.boolean().default(true),
    })
    .default({}),

  ga4: z
    .object({
      /** Enable Google Analytics 4 — more detailed but requires cookie consent */
      enabled: z.boolean().default(false),
      /** GA4 Measurement ID — find it in Google Analytics → Admin → Data Streams
       *  Looks like "G-XXXXXXXXXX" */
      measurementId: z.string().optional(),
    })
    .default({}),
});
export type AnalyticsConfig = z.infer<typeof AnalyticsConfigSchema>;

// -----------------------------------------------------------------------------
// Map Configuration
// -----------------------------------------------------------------------------

/**
 * Map embed configuration for the restaurant location.
 *
 * TWO OPTIONS:
 * - "osm" (OpenStreetMap): Free, no API key needed, no cookies, GDPR-safe
 * - "google": More feature-rich but loads a third-party iframe (needs consent)
 *
 * Both use a "click-to-load" pattern by default — the map is NOT loaded
 * until the user clicks a static placeholder. This improves page speed
 * and respects privacy (no third-party scripts until the user opts in).
 */
export const MapConfigSchema = z.object({
  /** Which map provider to use */
  provider: z.enum(['osm', 'google']).default('osm'),

  /** The embed URL for the map iframe.
   *  - For OSM: use https://www.openstreetmap.org/export/embed.html?...
   *  - For Google: use the "Share → Embed a map" URL from Google Maps */
  embedUrl: z.string().url().optional(),

  /** Direct link to the restaurant on Google Maps (always shown as fallback).
   *  Get it from Google Maps → Share → Copy link */
  googleMapsLink: z.string().url().optional(),

  /** Direct link to the restaurant on Apple Maps (optional, for iOS users) */
  appleMapsLink: z.string().url().optional(),
});
export type MapConfig = z.infer<typeof MapConfigSchema>;

// -----------------------------------------------------------------------------
// Reservation Configuration
// -----------------------------------------------------------------------------

/**
 * Reservation/booking widget configuration.
 * Supports multiple providers — each has its own widget that gets
 * embedded on the page.
 *
 * NOTE: This is a Phase 3 feature — for now, we support a simple
 * "Reserve" CTA button that links out to the booking platform.
 * The embedded widget adapters will be built later.
 */
export const ReservationConfigSchema = z.object({
  /** Enable the reservation module */
  enabled: z.boolean().default(false),

  /** Which booking platform the restaurant uses */
  provider: z
    .enum([
      'opentable', // OpenTable — large marketplace, per-cover fees
      'resy', // Resy — popular with upscale restaurants
      'sevenrooms', // SevenRooms — white-label, restaurant owns data
      'tock', // Tock — supports prepaid/ticketed dining
      'thefork', // TheFork — popular in continental Europe
      'custom', // Custom URL — for any other platform
    ])
    .optional(),

  /** Direct booking URL — used as the CTA link until embedded widgets are built */
  bookingUrl: z.string().url().optional(),

  /** Provider-specific ID (e.g., SevenRooms venueId, OpenTable restaurant ID) */
  providerId: z.string().optional(),
});
export type ReservationConfig = z.infer<typeof ReservationConfigSchema>;

// -----------------------------------------------------------------------------
// Developer Attribution & Watermark Configuration
// -----------------------------------------------------------------------------

/**
 * Developer and agency attribution configuration.
 * Controls branding in footers, robots.txt, llms.txt, humans.txt,
 * HTML meta tags, and Schema.org structured data.
 */
export const DeveloperConfigSchema = z.object({
  /** Developer / agency name (e.g. 'jeduapf') */
  name: z.string().default('jeduapf'),

  /** Portfolio or agency URL (e.g. 'https://portfolio.jeduapf.com') */
  url: z.string().url().default('https://portfolio.jeduapf.com'),

  /** Professional role or specialty */
  role: z.string().default('Restaurant Web Developer'),

  /** Whether to show a subtle "Website crafted by [name]" in the footer */
  showFooterCredit: z.boolean().default(true),
});
export type DeveloperConfig = z.infer<typeof DeveloperConfigSchema>;

// -----------------------------------------------------------------------------
// SEO Configuration
// -----------------------------------------------------------------------------

/**
 * SEO settings for the site.
 * These control the <title>, <meta description>, and structured data
 * that search engines and AI assistants use to understand and rank the site.
 */
export const SEOConfigSchema = z.object({
  /** Title template — %s is replaced with the page name.
   *  Example: "%s | Trattoria da Marco" → "Menu | Trattoria da Marco" */
  titleTemplate: z.string().default('%s'),

  /** Default meta description — shown in search results when no page-specific
   *  description is set. Keep it under 160 characters.
   *  Example: "Authentic Italian cuisine in the heart of Paris. Fresh pasta,
   *  wood-fired pizzas, and seasonal specials." */
  defaultDescription: z.string().max(160).default(''),

  /** Default social sharing image (Open Graph / Twitter Card).
   *  Used when no page-specific image is set. Should be 1200x630px. */
  ogImage: z.string().optional(),
});
export type SEOConfig = z.infer<typeof SEOConfigSchema>;

// -----------------------------------------------------------------------------
// Main Site Configuration Schema
// -----------------------------------------------------------------------------

/**
 * THE MASTER CONFIGURATION for a restaurant website.
 *
 * This is the ONLY file you need to change when creating a new restaurant site.
 * Everything else (components, styles, security, SEO) adapts automatically.
 *
 * WORKFLOW FOR A NEW CLIENT:
 * 1. Copy the `apps/site/` directory
 * 2. Edit `site.config.ts` with the restaurant's details
 * 3. Fill in `content/restaurant.json` and `content/menu.json`
 * 4. Add translations in `content/i18n/`
 * 5. Run `pnpm build` → deploy to Cloudflare Pages
 */
export const SiteConfigSchema = z
  .object({
    // ===== VISUAL STYLE =====

    /** Which style pack (visual theme) to use.
     *  This controls colors, fonts, spacing, and layout variants.
     *  Each pack is a CSS file that overrides the base.css tokens.
     *
     *  Available packs:
     *  - "warm-trattoria"    → Cream + terracotta + tomato red (Italian/Mediterranean)
     *  - "moody-fine-dining" → Charcoal + espresso + muted gold (upscale/evening)
     *  - "bold-fast-casual"  → White + near-black + orange-red (QSR/burgers)
     *  - "coastal-seafood"   → Sand + deep teal + coral (seafood/wine bars)
     *  - "artisan-cafe"      → Cream + roasted brown + dusty rose (coffee/bakery)
     *  - "fresh-healthy"     → White + deep sage + amber (salads/plant-based)
     */
    stylePack: z.string().default('warm-trattoria'),

    // ===== GEOGRAPHIC REGION =====

    /** The region where the restaurant operates.
     *  This setting controls MULTIPLE things automatically:
     *  - Which ALLERGEN REGULATION applies (EU-14 vs US-9)
     *  - Whether COOKIE CONSENT is required (EU = yes, US = varies)
     *  - Which DELIVERY PLATFORMS are shown by default
     *  - RTL layout support (MENA region)
     *
     *  Regions:
     *  - "eu"    → European Union (GDPR, EU-14 allergens, Deliveroo/Just Eat/Glovo)
     *  - "uk"    → United Kingdom (UK GDPR, similar to EU)
     *  - "us"    → United States (CCPA, FALCPA allergens, DoorDash/Uber Eats)
     *  - "latam" → Latin America (LGPD in Brazil, iFood/Rappi)
     *  - "asia"  → Asia (varies by country, Grab/Swiggy/Zomato)
     *  - "mena"  → Middle East & North Africa (RTL languages, Talabat)
     */
    region: z.enum(['eu', 'uk', 'us', 'latam', 'asia', 'mena']).default('eu'),

    // ===== LANGUAGE & LOCALIZATION =====

    /** The default language for the site.
     *  Uses ISO 639-1 codes: "en", "fr", "es", "pt", "de", "it", "ar", etc.
     *  The site will load in this language unless the user switches. */
    defaultLocale: z.string().default('en'),

    /** All languages available on the site.
     *  For each language listed here, you need a matching translation file
     *  in content/i18n/ (e.g., content/i18n/fr.json for French).
     *  The language switcher in the navbar will show all these options.
     *  hreflang tags are auto-generated for SEO. */
    locales: z.array(z.string()).min(1).default(['en']),

    /** Default currency for menu prices.
     *  ISO 4217 code: "EUR", "USD", "GBP", "BRL", "INR", etc.
     *  Used to format prices with the correct symbol (€, $, £, R$, ₹). */
    currency: z.string().length(3).default('EUR'),

    // ===== FEATURE INTEGRATIONS =====

    /** Reservation/booking configuration */
    reservations: ReservationConfigSchema.default({}),

    /** Delivery platform buttons */
    delivery: z
      .object({
        enabled: z.boolean().default(false),
        platforms: z.array(DeliveryPlatformSchema).default([]),
      })
      .default({}),

    /** Map embed configuration */
    maps: MapConfigSchema.default({}),

    /** Analytics configuration */
    analytics: AnalyticsConfigSchema.default({}),

    // ===== SEO =====

    /** Search engine optimization settings */
    seo: SEOConfigSchema.default({}),

    // ===== DEVELOPER ATTRIBUTION & WATERMARK =====

    /** Developer / agency branding for footer watermark, SEO, llms.txt, robots.txt, humans.txt */
    developer: DeveloperConfigSchema.default({}),

    // ===== ALLERGEN REGULATION =====

    /** Which allergen labeling regulation to follow.
     *  This determines which allergens are REQUIRED to be labeled on every dish.
     *
     *  - "eu-fic-1169" → EU Regulation 1169/2011: 14 mandatory allergens
     *  - "us-falcpa"   → US FALCPA + FASTER Act: 9 major allergens
     *  - "custom"      → Custom list (define your own)
     */
    allergenRegulation: z.enum(['eu-fic-1169', 'us-falcpa', 'custom']).default('eu-fic-1169'),
  })
  .refine((data) => data.locales.includes(data.defaultLocale), {
    message: 'defaultLocale must be included in locales array',
    path: ['defaultLocale'],
  });

export type SiteConfig = z.infer<typeof SiteConfigSchema>;
